import React, { useState } from 'react';
import VoiceInputButton from '../../VoiceInputButton';

interface StandardIntakeQuestionnaireProps {
  onComplete: (answers: StandardIntakeAnswers) => void;
}

export interface StandardIntakeAnswers {
  counselingReason: string;
  counselingGoals: string;
  previousMentalHealth: string;
  medications: string;
  prescribingMD: string;
  primaryCarePhysician: string;
  drinkAlcohol: string;
  useRecreationalDrugs: string;
  suicidalThoughts: string;
  suicideAttempt: string;
  harmOthersThoughts: string;
  psychiatricHospitalization: string;
  familyMentalHealth: string;
  relationshipStatus: string;
  livingSituation: string;
  educationLevel: string;
  occupation: string;
  pastSixMonthsSymptoms: string[];
  medicalConditions: string[];
  additionalInfo: string;
}

const StandardIntakeQuestionnaire: React.FC<StandardIntakeQuestionnaireProps> = ({ onComplete }) => {
  const [showReview, setShowReview] = useState(false);
  const [answers, setAnswers] = useState<StandardIntakeAnswers>({
    counselingReason: '',
    counselingGoals: '',
    previousMentalHealth: '',
    medications: '',
    prescribingMD: '',
    primaryCarePhysician: '',
    drinkAlcohol: '',
    useRecreationalDrugs: '',
    suicidalThoughts: '',
    suicideAttempt: '',
    harmOthersThoughts: '',
    psychiatricHospitalization: '',
    familyMentalHealth: '',
    relationshipStatus: '',
    livingSituation: '',
    educationLevel: '',
    occupation: '',
    pastSixMonthsSymptoms: [],
    medicalConditions: [],
    additionalInfo: ''
  });

  const yesNoQuestions = [
    { id: 'previousMentalHealth', text: 'Have you seen a mental health professional before?' },
    { id: 'drinkAlcohol', text: 'Do you drink alcohol?' },
    { id: 'useRecreationalDrugs', text: 'Do you use recreational drugs?' },
    { id: 'suicidalThoughts', text: 'Do you have suicidal thoughts?' },
    { id: 'suicideAttempt', text: 'Have you ever attempted suicide?' },
    { id: 'harmOthersThoughts', text: 'Do you have thoughts or urges to harm others?' },
    { id: 'psychiatricHospitalization', text: 'Have you ever been hospitalized for a psychiatric issue?' },
    { id: 'familyMentalHealth', text: 'Is there a history of mental illness in your family?' }
  ];

  const pastSixMonthsOptions = [
    'Increased appetite', 'Decreased appetite', 'Trouble concentrating', 'Difficulty sleeping',
    'Excessive sleep', 'Low motivation', 'Isolation from others', 'Fatigue/low energy',
    'Low self-esteem', 'Depressed mood', 'Tearful or crying spells', 'Anxiety',
    'Fear', 'Hopelessness', 'Panic', 'Other'
  ];

  const medicalConditionsOptions = [
    'Headache', 'High blood pressure', 'Gastritis or esophagitis', 'Hormone-related problems',
    'Head injury', 'Angina or chest pain', 'Irritable bowel', 'Chronic pain',
    'Loss of consciousness', 'Heart attack', 'Bone or joint problems', 'Seizures',
    'Kidney-related issues', 'Chronic fatigue', 'Dizziness', 'Faintness',
    'Heart valve problems', 'Urinary tract problems', 'Fibromyalgia', 'Numbness & tingling',
    'Shortness of breath', 'Diabetes', 'Hepatitis', 'Asthma', 'Arthritis',
    'Thyroid issues', 'HIV/AIDS', 'Cancer', 'Other'
  ];

  const handleTextChange = (field: keyof StandardIntakeAnswers, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field: 'pastSixMonthsSymptoms' | 'medicalConditions', value: string, checked: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], value]
        : prev[field].filter(item => item !== value)
    }));
  };

  const isAllRequiredFieldsCompleted = () => {
    const requiredFields = [
      'counselingReason', 'counselingGoals', 'previousMentalHealth',
      'drinkAlcohol', 'useRecreationalDrugs', 'suicidalThoughts', 'suicideAttempt',
      'harmOthersThoughts', 'psychiatricHospitalization', 'familyMentalHealth'
    ];
    return requiredFields.every(field => answers[field as keyof StandardIntakeAnswers] !== '');
  };

  const handleReviewAndContinue = () => {
    if (isAllRequiredFieldsCompleted()) {
      setShowReview(true);
    }
  };

  const handleSubmit = () => {
    onComplete(answers);
  };

  if (showReview) {
    return (
      <div className="app">
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          background: '#28527A',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          marginTop: '2rem'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
              The Real Therapists of Atlanta
            </h1>
            <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
              626 Main Street | Palmetto, GA. 30268 | (470).250.9113
            </p>
            <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
              Standard Intake Questionnaire Review
            </h1>
            <p style={{ color: '#6CA6C1', margin: 0 }}>
              Please review your answers before submitting
            </p>
          </div>

          {/* Review Content */}
          <div style={{
            background: '#113F67',
            borderRadius: '12px',
            border: '1px solid #6CA6C1',
            padding: '2rem',
            marginBottom: '2rem',
            maxHeight: '600px',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              Your Responses
            </h3>

            {/* Text Responses */}
            {answers.counselingReason && (
              <div style={{ marginBottom: '1rem', background: '#1f2937', padding: '1rem', borderRadius: '8px' }}>
                <strong style={{ color: '#f9fafb' }}>What brings you to counseling:</strong>
                <p style={{ color: '#d1d5db', margin: '0.5rem 0 0 0' }}>{answers.counselingReason}</p>
              </div>
            )}

            {answers.counselingGoals && (
              <div style={{ marginBottom: '1rem', background: '#1f2937', padding: '1rem', borderRadius: '8px' }}>
                <strong style={{ color: '#f9fafb' }}>Counseling goals:</strong>
                <p style={{ color: '#d1d5db', margin: '0.5rem 0 0 0' }}>{answers.counselingGoals}</p>
              </div>
            )}

            {/* Yes/No Questions */}
            {yesNoQuestions.map(question => (
              answers[question.id as keyof StandardIntakeAnswers] && (
                <div key={question.id} style={{ marginBottom: '0.5rem', background: '#1f2937', padding: '0.75rem', borderRadius: '8px' }}>
                  <span style={{ color: '#f9fafb', fontSize: '0.9rem' }}>{question.text}</span>
                  <span style={{ color: '#10b981', fontSize: '0.875rem', fontWeight: 'bold', marginLeft: '1rem' }}>
                    {answers[question.id as keyof StandardIntakeAnswers]}
                  </span>
                </div>
              )
            ))}

            {/* Checkboxes */}
            {answers.pastSixMonthsSymptoms.length > 0 && (
              <div style={{ marginBottom: '1rem', background: '#1f2937', padding: '1rem', borderRadius: '8px' }}>
                <strong style={{ color: '#f9fafb' }}>Symptoms in past 6 months:</strong>
                <p style={{ color: '#d1d5db', margin: '0.5rem 0 0 0' }}>{answers.pastSixMonthsSymptoms.join(', ')}</p>
              </div>
            )}

            {answers.medicalConditions.length > 0 && (
              <div style={{ marginBottom: '1rem', background: '#1f2937', padding: '1rem', borderRadius: '8px' }}>
                <strong style={{ color: '#f9fafb' }}>Medical conditions:</strong>
                <p style={{ color: '#d1d5db', margin: '0.5rem 0 0 0' }}>{answers.medicalConditions.join(', ')}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setShowReview(false)}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                border: 'none',
                background: '#374151',
                color: '#f9fafb',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Back to Edit
            </button>

            <button
              onClick={handleSubmit}
              style={{
                background: 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
                color: 'white',
                padding: '0.75rem 3rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
              }}
            >
              Submit & Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main Street | Palmetto, GA. 30268 | (470).250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Standard Intake Questionnaire
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please complete the questionnaire below
          </p>
        </div>

        {/* Question Form */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          padding: '2rem',
          marginBottom: '2rem',
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem' }}>
              <span style={{ color: '#ef4444' }}>*</span> Indicates a required field
            </p>
          </div>

          {/* Text Questions */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              <span style={{ color: '#ef4444' }}>*</span> What brings you to counseling at this time? Is there something specific, such as a particular event? Be as detailed as you can.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.counselingReason}
                onChange={(e) => handleTextChange('counselingReason', e.target.value)}
                style={{
                  flex: 1,
                  height: '120px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
                placeholder="Please describe what brings you to counseling..."
              />
              <VoiceInputButton onResult={(text) => handleTextChange('counselingReason', answers.counselingReason ? answers.counselingReason + ' ' + text : text)} size={20} />
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              <span style={{ color: '#ef4444' }}>*</span> What are your goals for counseling?
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.counselingGoals}
                onChange={(e) => handleTextChange('counselingGoals', e.target.value)}
                style={{
                  flex: 1,
                  height: '120px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
                placeholder="Please describe your goals for counseling..."
              />
              <VoiceInputButton onResult={(text) => handleTextChange('counselingGoals', answers.counselingGoals ? answers.counselingGoals + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Yes/No Questions */}
          {yesNoQuestions.map((question) => (
            <div key={question.id} style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '1rem' }}>
                <span style={{ color: '#ef4444' }}>*</span> {question.text}
              </h3>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {['Yes', 'No'].map((option) => (
                  <label key={option} style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#d1d5db',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    background: answers[question.id as keyof StandardIntakeAnswers] === option ? '#1f2937' : '#374151',
                    borderRadius: '8px',
                    border: answers[question.id as keyof StandardIntakeAnswers] === option ? '2px solid #3b82f6' : '1px solid #4b5563',
                    transition: 'all 0.2s ease'
                  }}>
                    <input
                      type="radio"
                      name={question.id}
                      value={option}
                      checked={answers[question.id as keyof StandardIntakeAnswers] === option}
                      onChange={(e) => handleTextChange(question.id as keyof StandardIntakeAnswers, e.target.value)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {/* Additional Text Fields */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              Specify all medications and supplements you are presently taking and for what reason.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.medications}
                onChange={(e) => handleTextChange('medications', e.target.value)}
                style={{
                  flex: 1,
                  height: '100px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('medications', answers.medications ? answers.medications + ' ' + text : text)} size={20} />
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              If taking prescription medication, who is your prescribing MD? Please include type of MD, name and phone number.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.prescribingMD}
                onChange={(e) => handleTextChange('prescribingMD', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('prescribingMD', answers.prescribingMD ? answers.prescribingMD + ' ' + text : text)} size={20} />
            </div>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              Who is your primary care physician? Please include type of MD, name and phone number.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.primaryCarePhysician}
                onChange={(e) => handleTextChange('primaryCarePhysician', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('primaryCarePhysician', answers.primaryCarePhysician ? answers.primaryCarePhysician + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Relationship Status */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              If you are in a relationship, please describe the nature of the relationship and months or years together.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.relationshipStatus}
                onChange={(e) => handleTextChange('relationshipStatus', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('relationshipStatus', answers.relationshipStatus ? answers.relationshipStatus + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Living Situation */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              Describe your current living situation. Do you live alone, with others, with family, etc...
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.livingSituation}
                onChange={(e) => handleTextChange('livingSituation', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('livingSituation', answers.livingSituation ? answers.livingSituation + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Education Level */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              What is your level of education? Highest grade/degree and type of degree.
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.educationLevel}
                onChange={(e) => handleTextChange('educationLevel', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('educationLevel', answers.educationLevel ? answers.educationLevel + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Occupation */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              What is your current occupation? What do you do? How long have you been doing it?
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.occupation}
                onChange={(e) => handleTextChange('occupation', e.target.value)}
                style={{
                  flex: 1,
                  height: '80px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('occupation', answers.occupation ? answers.occupation + ' ' + text : text)} size={20} />
            </div>
          </div>

          {/* Past Six Months Symptoms */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '1rem' }}>
              Please check any of the following you have experienced in the past six months
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.5rem' }}>
              {pastSixMonthsOptions.map((option) => (
                <label key={option} style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  background: '#374151',
                  borderRadius: '6px'
                }}>
                  <input
                    type="checkbox"
                    checked={answers.pastSixMonthsSymptoms.includes(option)}
                    onChange={(e) => handleCheckboxChange('pastSixMonthsSymptoms', option, e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          {/* Medical Conditions */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '1rem' }}>
              Please check any of the following that apply
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0.5rem' }}>
              {medicalConditionsOptions.map((option) => (
                <label key={option} style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  background: '#374151',
                  borderRadius: '6px'
                }}>
                  <input
                    type="checkbox"
                    checked={answers.medicalConditions.includes(option)}
                    onChange={(e) => handleCheckboxChange('medicalConditions', option, e.target.checked)}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          {/* Additional Information */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ color: '#f9fafb', fontSize: '1rem', marginBottom: '0.5rem', display: 'block' }}>
              What else would you like me to know?
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <textarea
                value={answers.additionalInfo}
                onChange={(e) => handleTextChange('additionalInfo', e.target.value)}
                style={{
                  flex: 1,
                  height: '120px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
              <VoiceInputButton onResult={(text) => handleTextChange('additionalInfo', answers.additionalInfo ? answers.additionalInfo + ' ' + text : text)} size={20} />
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleReviewAndContinue}
            disabled={!isAllRequiredFieldsCompleted()}
            style={{
              background: isAllRequiredFieldsCompleted()
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: isAllRequiredFieldsCompleted() ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: isAllRequiredFieldsCompleted()
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none',
              opacity: isAllRequiredFieldsCompleted() ? 1 : 0.6
            }}
          >
            Review & Continue
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Required Fields Completed: {Object.values(answers).filter((answer, index) =>
              index < 10 && answer !== ''
            ).length} of 10
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandardIntakeQuestionnaire;